import React, { useState, useEffect, useRef } from "react";
import { X, Ruler, Info } from "lucide-react";
import { useScrollLock } from "../hooks/useScrollLock";
import { gsap } from "gsap";

interface SizeGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  productCategory?: string;
}

const SizeGuideModal: React.FC<SizeGuideModalProps> = ({
  isOpen,
  onClose,
  productCategory = "clothing",
}) => {
  const [activeTab, setActiveTab] = useState<"chart" | "measure">("chart");
  const backdropRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Lock scroll when modal is open
  useScrollLock(isOpen);

  // Animate modal
  useEffect(() => {
    if (isOpen) {
      if (backdropRef.current) {
        gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2 });
      }
      if (modalRef.current) {
        gsap.fromTo(
          modalRef.current,
          { opacity: 0, scale: 0.95, y: 20 },
          { opacity: 1, scale: 1, y: 0, duration: 0.3, ease: "back.out(1.7)" }
        );
      }
    } else {
      if (backdropRef.current) {
        gsap.to(backdropRef.current, { opacity: 0, duration: 0.2 });
      }
      if (modalRef.current) {
        gsap.to(modalRef.current, { opacity: 0, scale: 0.95, y: 20, duration: 0.3, ease: "power2.in" });
      }
    }
  }, [isOpen]);

  // Men's Tops Size Chart
  const mensTopsSizeChart = {
    title: "Men's Tops",
    headers: ["Size", "Chest (in)", "Waist (in)", "Hip (in)"],
    measurements: [
      { size: "XS", chest: "33-35", waist: "27-29", hip: "33-35" },
      { size: "S", chest: "36-38", waist: "30-32", hip: "36-38" },
      { size: "M", chest: "39-41", waist: "33-35", hip: "39-41" },
      { size: "L", chest: "42-44", waist: "36-38", hip: "42-44" },
      { size: "XL", chest: "45-48", waist: "39-42", hip: "45-48" },
      { size: "XXL", chest: "49-52", waist: "43-46", hip: "49-52" },
    ],
  };

  // Women's Tops Size Chart
  const womensTopsSizeChart = {
    title: "Women's Tops",
    headers: ["Size", "Bust (in)", "Waist (in)", "Hip (in)"],
    measurements: [
      { size: "XS", bust: "31.5-33", waist: "23-25", hip: "33-35" },
      { size: "S", bust: "34-35", waist: "26-27", hip: "36-37" },
      { size: "M", bust: "36-37", waist: "28-29", hip: "38-39" },
      { size: "L", bust: "38.5-40", waist: "30.5-32", hip: "40.5-42" },
      { size: "XL", bust: "41.5-43", waist: "33.5-35", hip: "43.5-45" },
      { size: "XXL", bust: "44-46", waist: "36-38", hip: "46-48" },
    ],
  };

  // Measurement instructions
  const measurementInstructions = [
    {
      title: "Chest",
      description: "Measure around the fullest part of your chest, keeping the tape horizontal.",
      icon: "chest",
    },
    {
      title: "Waist",
      description: "Measure around your natural waistline, at the narrowest part of your torso.",
      icon: "waist",
    },
    {
      title: "Hip",
      description: "Measure around the fullest part of your hips, keeping the tape horizontal.",
      icon: "hip",
    },
    {
      title: "Inseam (for Pants)",
      description: "Measure from the crotch to the bottom of your ankle along the inside of your leg.",
      icon: "inseam",
    },
  ];

  // Measurement tips
  const measurementTips = [
    "Use a flexible measuring tape",
    "Measure over fitted clothing or underwear",
    "Keep the tape snug but not tight",
    "Stand naturally with arms at your sides",
    "Have someone help you for more accurate measurements",
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 z-40"
        style={{ willChange: 'opacity' }}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed inset-0 flex items-center justify-center p-4 z-50 pointer-events-none"
      >
            <div 
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-3xl max-h-[90vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto"
            >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-red-600 rounded-lg flex items-center justify-center">
                  <Ruler className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Size Guide</h2>
              </div>
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-white flex-shrink-0">
              <button
                onClick={() => setActiveTab("chart")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === "chart"
                    ? "bg-red-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                Size Chart
              </button>
              <button
                onClick={() => setActiveTab("measure")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === "measure"
                    ? "bg-red-600 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                How to Measure
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 overscroll-contain">
              {activeTab === "chart" ? (
                <div className="space-y-6">
                  {/* Men's Tops Size Chart */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">
                      {mensTopsSizeChart.title}
                    </h3>
                    <div className="overflow-x-auto -mx-4 px-4">
                      <table className="w-full border border-gray-200 rounded-lg overflow-hidden min-w-full">
                        <thead>
                          <tr className="bg-red-50">
                            {mensTopsSizeChart.headers.map((header, index) => (
                              <th
                                key={index}
                                className="px-3 py-2.5 text-left text-xs font-medium text-red-600 border-b border-red-100 whitespace-nowrap"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {mensTopsSizeChart.measurements.map(
                            (measurement, index) => (
                              <tr
                                key={index}
                                className={`${
                                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                } hover:bg-gray-100 transition-colors`}
                              >
                                <td className="px-3 py-2.5 text-xs font-medium text-gray-900 border-b border-gray-100">
                                  {measurement.size}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-gray-600 border-b border-gray-100">
                                  {measurement.chest}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-gray-600 border-b border-gray-100">
                                  {measurement.waist}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-gray-600 border-b border-gray-100">
                                  {measurement.hip}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Women's Tops Size Chart */}
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">
                      {womensTopsSizeChart.title}
                    </h3>
                    <div className="overflow-x-auto -mx-4 px-4">
                      <table className="w-full border border-gray-200 rounded-lg overflow-hidden min-w-full">
                        <thead>
                          <tr className="bg-red-50">
                            {womensTopsSizeChart.headers.map((header, index) => (
                              <th
                                key={index}
                                className="px-3 py-2.5 text-left text-xs font-medium text-red-600 border-b border-red-100 whitespace-nowrap"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {womensTopsSizeChart.measurements.map(
                            (measurement, index) => (
                              <tr
                                key={index}
                                className={`${
                                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                } hover:bg-gray-100 transition-colors`}
                              >
                                <td className="px-3 py-2.5 text-xs font-medium text-gray-900 border-b border-gray-100">
                                  {measurement.size}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-gray-600 border-b border-gray-100">
                                  {measurement.bust}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-gray-600 border-b border-gray-100">
                                  {measurement.waist}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-gray-600 border-b border-gray-100">
                                  {measurement.hip}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Tip */}
                  <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-red-700">
                        Tip: Measure your chest/bust at the fullest part for best fit.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">
                    How to Measure
                  </h3>

                  {/* Measurement Instructions */}
                  <div className="space-y-3">
                    {measurementInstructions.map((instruction, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Ruler className="w-5 h-5 text-red-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 mb-1">
                              {instruction.title}
                            </h4>
                            <p className="text-xs text-gray-600 leading-relaxed">
                              {instruction.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Measurement Tips */}
                  <div className="bg-red-50 rounded-lg p-4 border border-red-100 mt-6">
                    <div className="flex items-start gap-2 mb-3">
                      <Info className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <h4 className="text-sm font-semibold text-red-700">
                        Measurement Tips
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {measurementTips.map((tip, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-2 text-xs text-red-700"
                        >
                          <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-1.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
    </>
  );
};

export default SizeGuideModal;
